import { forwardRef, type ButtonHTMLAttributes } from 'react';
import clsx from 'clsx';

type Variant = 'primary' | 'ghost' | 'danger' | 'outline';
type Size = 'sm' | 'md' | 'lg';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  full?: boolean;
};

const sizes: Record<Size, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-[15px]',
  lg: 'h-14 px-6 text-base',
};

const variants: Record<Variant, string> = {
  primary:
    'bg-accent text-white shadow-glow hover:bg-accent-hi active:scale-[0.98]',
  ghost: 'bg-transparent text-ink-1 hover:bg-white/[0.06]',
  danger: 'bg-danger/90 text-white hover:bg-danger active:scale-[0.98]',
  outline:
    'border border-white/15 bg-white/[0.02] text-ink-1 hover:bg-white/[0.06]',
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { className, variant = 'primary', size = 'md', full, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-2xl font-medium transition focus-ring',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
        full && 'w-full',
        sizes[size],
        variants[variant],
        className,
      )}
      {...rest}
    />
  );
});
